/**
 * Inspeção de um pneu (desktop / mapa). Mesmos campos da ronda do app: sulco,
 * pressão, condição, problemas, observação e foto. A condição gravada pode
 * subir em relação à escolhida (sulco no limite, corte/bolha) — a API decide e
 * a tela mostra o resultado. Problema grave oferece abrir O.S. (manual).
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { TireConditionDto, TireProblemDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { IconCamera, IconX } from "../../components/icons";
import { compressPhoto } from "../maintenance/photo-compress";
import { useCapabilities } from "../maintenance/useCapabilities";
import { ConditionBadge, PROBLEM_LABEL, TIRE_CONDITION } from "./tire-ui";

export const PROBLEMS: TireProblemDto[] = ["low_pressure", "irregular_wear", "cut", "bulge", "low_tread", "damage", "other"];
const CONDITIONS: TireConditionDto[] = ["normal", "attention", "critical"];

export interface InspectionFields {
  tread: string;
  pressure: string;
  condition: TireConditionDto;
  problems: TireProblemDto[];
  notes: string;
}

export const emptyInspection = (): InspectionFields => ({ tread: "", pressure: "", condition: "normal", problems: [], notes: "" });

export const toNumber = (s: string) => (s.trim() === "" ? null : Number(s.replace(",", ".")));

/** Campos da inspeção — os mesmos no modal e no passo a passo do app. */
export function InspectionFieldsForm({ value, onChange, compact }: {
  value: InspectionFields;
  onChange: (v: InspectionFields) => void;
  compact?: boolean;
}) {
  const set = (patch: Partial<InspectionFields>) => onChange({ ...value, ...patch });
  const toggle = (p: TireProblemDto) =>
    set({ problems: value.problems.includes(p) ? value.problems.filter((x) => x !== p) : [...value.problems, p] });
  const tone: Record<TireConditionDto, string> = {
    normal: "bg-tm-500 border-tm-500 text-white",
    attention: "bg-warning-500 border-warning-500 text-white",
    critical: "bg-danger-600 border-danger-600 text-white",
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-gray-500">Sulco (mm)</span>
          <input className="input w-full mt-0.5 text-lg" inputMode="decimal" value={value.tread} placeholder="Ex.: 6,5"
            onChange={(e) => set({ tread: e.target.value.replace(/[^\d,.]/g, "") })} />
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Pressão (PSI)</span>
          <input className="input w-full mt-0.5 text-lg" inputMode="decimal" value={value.pressure} placeholder="Ex.: 105"
            onChange={(e) => set({ pressure: e.target.value.replace(/[^\d,.]/g, "") })} />
        </label>
      </div>
      <div>
        <span className="text-xs text-gray-500">Condição</span>
        <div className="mt-1 grid grid-cols-3 gap-2">
          {CONDITIONS.map((c) => (
            <button key={c} type="button" onClick={() => set({ condition: c })}
              className={`rounded-lg border px-2 py-2.5 text-sm font-medium ${value.condition === c ? tone[c] : "bg-white border-surface-border text-gray-600"}`}>
              {TIRE_CONDITION[c].label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <span className="text-xs text-gray-500">Problemas encontrados</span>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {PROBLEMS.map((p) => (
            <button key={p} type="button" onClick={() => toggle(p)}
              className={`rounded-full border px-3 py-1.5 text-xs ${value.problems.includes(p) ? "bg-gray-900 border-gray-900 text-white" : "bg-white border-surface-border text-gray-600"}`}>
              {PROBLEM_LABEL[p]}
            </button>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="text-xs text-gray-500">Observação</span>
        <textarea className="input w-full mt-0.5" rows={compact ? 1 : 2} value={value.notes} maxLength={2000} onChange={(e) => set({ notes: e.target.value })} />
      </label>
    </div>
  );
}

export function InspectTireModal({ tireId, tireCode, positionLabel, plate, onClose }: {
  tireId: string;
  tireCode: string;
  positionLabel?: string | null;
  plate?: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useCapabilities();
  const [v, setV] = useState<InspectionFields>(emptyInspection());
  const [km, setKm] = useState("");
  const [foto, setFoto] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<{ id: string; condition: TireConditionDto } | null>(null);

  const salvar = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ id: string; condition: TireConditionDto }>(`/fleet/tires/${tireId}/inspections`, {
        treadDepthMm: toNumber(v.tread), pressurePsi: toNumber(v.pressure), condition: v.condition,
        problems: v.problems, notes: v.notes.trim() || null, odometerKm: km ? Number(km) : null,
      });
      if (foto) await api.upload(`/fleet/tire-inspections/${r.id}/attachments`, new File([await compressPhoto(foto)], foto.name || "foto.jpg", { type: "image/jpeg" }));
      return r;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["tires"] });
      setFeito(r);
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar a inspeção."),
  });

  const abrirOS = useMutation({
    mutationFn: () => api.post<{ workOrderId: string }>(`/fleet/tire-inspections/${feito!.id}/work-order`, {}),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["maintenance"] }); navigate(`/frota/manutencao/ordens/${r.workOrderId}`); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível abrir a O.S."),
  });

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-gray-900/40" role="dialog" aria-modal="true" aria-label="Inspecionar pneu">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-xl shadow-xl">
        <div className="flex items-start justify-between px-5 py-4 border-b border-surface-border">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Inspecionar pneu {tireCode}</h3>
            {(plate || positionLabel) && <p className="text-xs text-gray-500">{[plate, positionLabel].filter(Boolean).join(" · ")}</p>}
          </div>
          <button className="btn-ghost p-1" onClick={onClose} aria-label="Fechar"><IconX className="w-5 h-5" /></button>
        </div>

        {feito ? (
          <div className="p-5 space-y-4 text-center">
            <p className="text-sm text-gray-700">Inspeção registrada.</p>
            <p className="text-sm">Condição gravada: <ConditionBadge condition={feito.condition} /></p>
            {feito.condition !== v.condition && (
              <p className="text-xs text-gray-500">A condição subiu por causa do sulco medido ou dos problemas marcados.</p>
            )}
            {erro && <p className="text-sm text-danger-600">{erro}</p>}
            <div className="flex justify-center gap-2">
              {feito.condition !== "normal" && can("workorder.manage") && plate && (
                <button className="btn-secondary" disabled={abrirOS.isPending} onClick={() => abrirOS.mutate()}>
                  {abrirOS.isPending ? "Abrindo…" : "Abrir O.S."}
                </button>
              )}
              <button className="btn-primary" onClick={onClose}>Concluir</button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-5 space-y-3">
              <InspectionFieldsForm value={v} onChange={setV} />
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-gray-500">KM do veículo (opcional)</span>
                  <input className="input w-full mt-0.5" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value.replace(/\D/g, ""))} />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500">Foto</span>
                  <span className="btn-secondary w-full mt-0.5 justify-center cursor-pointer">
                    <IconCamera className="w-4 h-4" /> {foto ? "Trocar foto" : "Adicionar"}
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFoto(e.target.files?.[0] ?? null)} />
                  </span>
                  {foto && <span className="block text-xs text-gray-500 mt-1 truncate">{foto.name}</span>}
                </label>
              </div>
              {erro && <p className="text-sm text-danger-600">{erro}</p>}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-surface-border">
              <button className="btn-secondary" onClick={onClose}>Cancelar</button>
              <button className="btn-primary" disabled={salvar.isPending} onClick={() => { setErro(null); salvar.mutate(); }}>
                {salvar.isPending ? "Salvando…" : "Salvar inspeção"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
